// src/features/member/pages/UserProfilePage.tsx

import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useDispatch,
} from 'react-redux';

import {
  useNavigate,
} from 'react-router-dom';

import type {
  AppDispatch,
} from '../../../app/store';

import {
  setUser,
} from '../../auth/authSlice';

import {
  profileService,
} from '../services/profileService';

import type {
  NicknameCheckStatus,
  PasswordFormData,
  PasswordUpdateRequest,
  ProfileFormData,
  ProfileTab,
  ProfileUpdateRequest,
  StoreListItem,
  ToastMessage,
  ToastType,
  UserProfile,
} from '../types/profile.types';

import {
  getErrorMessage,
  getErrorStatus,
  isNicknameDuplicated,
  validateNickname,
  validatePassword,
} from '../utils/profile.utils';


interface PasswordInputProps {
  id: keyof PasswordFormData;

  label: string;

  value: string;

  placeholder: string;

  visible: boolean;

  autoComplete: string;

  onVisibleChange: () => void;

  onChange: (
    value: string,
  ) => void;
}


export default function UserProfilePage():
  React.JSX.Element {
  const navigate =
    useNavigate();

  const dispatch =
    useDispatch<AppDispatch>();


  /**
   * =========================================================
   * 탭
   * =========================================================
   */
  const [
    activeTab,
    setActiveTab,
  ] =
    useState<ProfileTab>(
      'profile',
    );


  /**
   * =========================================================
   * 사용자
   * =========================================================
   */
  const [
    user,
    setProfileUser,
  ] =
    useState<UserProfile | null>(
      null,
    );


  /**
   * =========================================================
   * 프로필 Form
   * =========================================================
   */
  const [
    profileForm,
    setProfileForm,
  ] =
    useState<ProfileFormData>({
      userNickname: '',
      storeId: 0,
    });


  /**
   * =========================================================
   * 비밀번호 Form
   * =========================================================
   */
  const [
    passwordForm,
    setPasswordForm,
  ] =
    useState<PasswordFormData>({
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    });


  /**
   * =========================================================
   * 매장 목록
   * =========================================================
   */
  const [
    storeList,
    setStoreList,
  ] =
    useState<StoreListItem[]>(
      [],
    );


  /**
   * =========================================================
   * 닉네임 중복 상태
   * =========================================================
   */
  const [
    nicknameStatus,
    setNicknameStatus,
  ] =
    useState<NicknameCheckStatus>(
      'unchecked',
    );


  const [
    nicknameMessage,
    setNicknameMessage,
  ] =
    useState('');


  /**
   * =========================================================
   * Loading
   * =========================================================
   */
  const [
    isLoading,
    setIsLoading,
  ] =
    useState(true);


  const [
    isLoadingStores,
    setIsLoadingStores,
  ] =
    useState(false);


  const [
    isSavingProfile,
    setIsSavingProfile,
  ] =
    useState(false);


  const [
    isSavingPassword,
    setIsSavingPassword,
  ] =
    useState(false);


  /**
   * =========================================================
   * Error
   * =========================================================
   */
  const [
    profileError,
    setProfileError,
  ] =
    useState('');


  const [
    passwordError,
    setPasswordError,
  ] =
    useState('');


  const [
    storeError,
    setStoreError,
  ] =
    useState('');


  /**
   * =========================================================
   * Toast
   * =========================================================
   */
  const [
    toast,
    setToast,
  ] =
    useState<ToastMessage | null>(
      null,
    );


  /**
   * =========================================================
   * Password visibility
   * =========================================================
   */
  const [
    showCurrentPassword,
    setShowCurrentPassword,
  ] =
    useState(false);


  const [
    showNewPassword,
    setShowNewPassword,
  ] =
    useState(false);


  const [
    showNewPasswordConfirm,
    setShowNewPasswordConfirm,
  ] =
    useState(false);


  /**
   * =========================================================
   * 비밀번호 일치 여부
   * =========================================================
   */
  const newPasswordMatches =
    passwordForm
      .newPasswordConfirm
      .length >
      0 &&
    passwordForm
      .newPassword ===
      passwordForm
        .newPasswordConfirm;


  const newPasswordMismatch =
    passwordForm
      .newPasswordConfirm
      .length >
      0 &&
    passwordForm
      .newPassword !==
      passwordForm
        .newPasswordConfirm;


  /**
   * =========================================================
   * 프로필 변경 여부
   * =========================================================
   */
  const nicknameChanged =
    useMemo(
      () => {
        if (!user) {
          return false;
        }


        return (
          profileForm
            .userNickname
            .trim() !==
          user.userNickname
            .trim()
        );
      },
      [
        profileForm.userNickname,
        user,
      ],
    );


  const storeChanged =
    useMemo(
      () => {
        if (!user) {
          return false;
        }


        return (
          profileForm.storeId !==
          user.storeId
        );
      },
      [
        profileForm.storeId,
        user,
      ],
    );


  const profileChanged =
    nicknameChanged ||
    storeChanged;


  /**
   * =========================================================
   * 선택 매장 이름
   * =========================================================
   */
  const selectedStoreName =
    useMemo(
      () => {
        return (
          storeList.find(
            (
              store,
            ) =>
              store.storeId ===
              profileForm.storeId,
          )?.storeName ??
          user?.storeName ??
          '-'
        );
      },
      [
        profileForm.storeId,
        storeList,
        user?.storeName,
      ],
    );


  /**
   * =========================================================
   * 공통 Style
   * =========================================================
   */
  const inputClassName = `
    min-h-11 w-full rounded-lg border border-gray-300
    bg-white px-3.5 py-2.5 text-sm text-gray-900
    outline-none transition placeholder:text-gray-400
    focus:border-main focus:ring-2 focus:ring-main/20
    disabled:cursor-not-allowed disabled:bg-gray-100
    disabled:text-gray-500
    sm:min-h-12 sm:rounded-xl sm:px-4 sm:py-3
  `;


  const readOnlyInputClassName = `
    min-h-11 w-full rounded-lg border border-gray-200
    bg-gray-50 px-3.5 py-2.5 text-sm text-gray-500
    outline-none
    sm:min-h-12 sm:rounded-xl sm:px-4 sm:py-3
  `;


  const labelClassName =
    'mb-1.5 block text-sm font-semibold text-gray-700 sm:mb-2';


  /**
   * =========================================================
   * Toast
   * =========================================================
   */
  const showToast = (
    type: ToastType,
    message: string,
  ): void => {
    setToast({
      type,
      message,
    });


    window.setTimeout(
      () => {
        setToast(
          null,
        );
      },
      3000,
    );
  };


  /**
   * =========================================================
   * 매장 목록 조회
   *
   * Page에서는 API를 직접 호출하지 않습니다.
   * =========================================================
   */
  const fetchStores =
    useCallback(
      async (): Promise<
        StoreListItem[]
      > => {
        try {
          setIsLoadingStores(
            true,
          );

          setStoreError(
            '',
          );


          const stores =
            await profileService
              .getStores();


          setStoreList(
            stores,
          );


          if (
            stores.length ===
            0
          ) {
            setStoreError(
              '선택할 수 있는 매장이 없습니다.',
            );
          }


          return stores;

        } catch (
          error: unknown
        ) {
          console.error(
            '매장 목록 조회 실패:',
            error,
          );


          setStoreList(
            [],
          );


          setStoreError(
            getErrorMessage(
              error,
              '매장 목록을 불러오지 못했습니다.',
            ),
          );


          return [];

        } finally {
          setIsLoadingStores(
            false,
          );
        }
      },
      [],
    );


  /**
   * =========================================================
   * 최초 프로필 데이터 조회
   *
   * 내부 API 처리는 profileService 담당
   * =========================================================
   */
  useEffect(
    () => {
      let mounted =
        true;


      const initialize =
        async (): Promise<void> => {
          try {
            setIsLoading(
              true,
            );

            setProfileError(
              '',
            );

            setStoreError(
              '',
            );


            const data =
              await profileService
                .getProfilePageData();


            if (!mounted) {
              return;
            }


            const {
              user:
                userData,
              stores,
            } =
              data;


            setProfileUser(
              userData,
            );


            setStoreList(
              stores,
            );


            setProfileForm({
              userNickname:
                userData.userNickname ??
                '',

              storeId:
                userData.storeId ??
                0,
            });


            setNicknameStatus(
              'available',
            );


            setNicknameMessage(
              '현재 사용 중인 닉네임입니다.',
            );


            if (
              stores.length ===
              0
            ) {
              setStoreError(
                '선택할 수 있는 매장이 없습니다.',
              );
            }

          } catch (
            error: unknown
          ) {
            if (!mounted) {
              return;
            }


            console.error(
              '프로필 초기 데이터 조회 실패:',
              error,
            );


            setProfileUser(
              null,
            );


            setProfileError(
              getErrorMessage(
                error,
                '프로필 정보를 불러오지 못했습니다.',
              ),
            );

          } finally {
            if (mounted) {
              setIsLoading(
                false,
              );
            }
          }
        };


      void initialize();


      return () => {
        mounted =
          false;
      };
    },
    [],
  );


  /**
   * =========================================================
   * 탭 변경
   * =========================================================
   */
  const handleTabChange = (
    tab: ProfileTab,
  ): void => {
    setActiveTab(
      tab,
    );


    if (
      tab ===
      'profile'
    ) {
      setPasswordError(
        '',
      );

      return;
    }


    setProfileError(
      '',
    );
  };


  /**
   * =========================================================
   * 닉네임 변경
   * =========================================================
   */
  const handleNicknameChange = (
    value: string,
  ): void => {
    const nextNickname =
      value.slice(
        0,
        12,
      );


    setProfileForm(
      (
        previous,
      ) => ({
        ...previous,

        userNickname:
          nextNickname,
      }),
    );


    setProfileError(
      '',
    );


    if (
      nextNickname.trim() ===
      user?.userNickname.trim()
    ) {
      setNicknameStatus(
        'available',
      );


      setNicknameMessage(
        '현재 사용 중인 닉네임입니다.',
      );


      return;
    }


    setNicknameStatus(
      'unchecked',
    );


    setNicknameMessage(
      '',
    );
  };


  /**
   * =========================================================
   * 닉네임 중복 확인
   * =========================================================
   */
  const handleNicknameCheck =
    async (): Promise<void> => {
      const nickname =
        profileForm
          .userNickname
          .trim();


      const validationMessage =
        validateNickname(
          nickname,
        );


      if (
        validationMessage
      ) {
        setNicknameStatus(
          'unchecked',
        );


        setNicknameMessage(
          validationMessage,
        );


        return;
      }


      if (
        nickname ===
        user?.userNickname.trim()
      ) {
        setNicknameStatus(
          'available',
        );


        setNicknameMessage(
          '현재 사용 중인 닉네임입니다.',
        );


        return;
      }


      try {
        setNicknameStatus(
          'checking',
        );


        setNicknameMessage(
          '닉네임 중복 여부를 확인하고 있습니다.',
        );


        const result =
          await profileService
            .checkNickname(
              nickname,
            );


        const duplicated =
          isNicknameDuplicated(
            result,
          );


        if (
          duplicated
        ) {
          setNicknameStatus(
            'duplicate',
          );


          setNicknameMessage(
            result.message ??
              '이미 사용 중인 닉네임입니다.',
          );


          return;
        }


        setNicknameStatus(
          'available',
        );


        setNicknameMessage(
          result.message ??
            '사용할 수 있는 닉네임입니다.',
        );

      } catch (
        error: unknown
      ) {
        console.error(
          '닉네임 중복확인 실패:',
          error,
        );


        setNicknameStatus(
          'unchecked',
        );


        setNicknameMessage(
          getErrorMessage(
            error,
            '닉네임 중복확인에 실패했습니다.',
          ),
        );
      }
    };


  /**
   * =========================================================
   * 프로필 Validation
   * =========================================================
   */
  const validateProfile =
    (): string | null => {
      const nickname =
        profileForm
          .userNickname
          .trim();


      const nicknameValidation =
        validateNickname(
          nickname,
        );


      if (
        nicknameValidation
      ) {
        return nicknameValidation;
      }


      if (
        nicknameChanged &&
        nicknameStatus !==
          'available'
      ) {
        return '변경할 닉네임의 중복확인을 완료해주세요.';
      }


      if (
        !Number.isInteger(
          profileForm.storeId,
        ) ||
        profileForm.storeId <=
          0
      ) {
        return '소속 매장을 선택해주세요.';
      }


      const storeExists =
        storeList.some(
          (
            store,
          ) =>
            store.storeId ===
            profileForm.storeId,
        );


      if (
        !storeExists
      ) {
        return '올바른 소속 매장을 선택해주세요.';
      }


      if (
        !profileChanged
      ) {
        return '변경된 프로필 정보가 없습니다.';
      }


      return null;
    };


  /**
   * =========================================================
   * 프로필 저장
   * =========================================================
   */
  const handleProfileSubmit =
    async (
      event:
        FormEvent<HTMLFormElement>,
    ): Promise<void> => {
      event.preventDefault();


      if (
        isSavingProfile
      ) {
        return;
      }


      setProfileError(
        '',
      );


      const validationMessage =
        validateProfile();


      if (
        validationMessage
      ) {
        setProfileError(
          validationMessage,
        );


        return;
      }


      const request:
        ProfileUpdateRequest = {
          userNickname:
            profileForm
              .userNickname
              .trim(),

          storeId:
            profileForm.storeId,
        };


      try {
        setIsSavingProfile(
          true,
        );


        const responseUser =
          await profileService
            .updateProfile(
              request,
            );


        /**
         * 백엔드에서 수정된 User를
         * 반환하지 않는 경우에도
         * 현재 데이터 기준으로 갱신합니다.
         */
        const updatedUser:
          UserProfile =
          responseUser ?? {
            ...user!,

            ...request,

            storeName:
              selectedStoreName,
          };


        setProfileUser(
          updatedUser,
        );


        dispatch(
          setUser(
            updatedUser,
          ),
        );


        setProfileForm({
          userNickname:
            updatedUser
              .userNickname,

          storeId:
            updatedUser
              .storeId,
        });


        setNicknameStatus(
          'available',
        );


        setNicknameMessage(
          '현재 사용 중인 닉네임입니다.',
        );


        showToast(
          'success',
          '프로필 정보가 수정되었습니다.',
        );

      } catch (
        error: unknown
      ) {
        console.error(
          '프로필 수정 실패:',
          error,
        );


        const message =
          getErrorMessage(
            error,
            '프로필 수정에 실패했습니다.',
          );


        setProfileError(
          message,
        );


        showToast(
          'error',
          message,
        );

      } finally {
        setIsSavingProfile(
          false,
        );
      }
    };


  /**
   * =========================================================
   * 비밀번호 변경
   * =========================================================
   */
  const handlePasswordSubmit =
    async (
      event:
        FormEvent<HTMLFormElement>,
    ): Promise<void> => {
      event.preventDefault();


      if (
        isSavingPassword
      ) {
        return;
      }


      setPasswordError(
        '',
      );


      const validationMessage =
        validatePassword(
          passwordForm,
        );


      if (
        validationMessage
      ) {
        setPasswordError(
          validationMessage,
        );


        return;
      }


      const request:
        PasswordUpdateRequest = {
          currentPassword:
            passwordForm
              .currentPassword,

          newPassword:
            passwordForm
              .newPassword,
        };


      try {
        setIsSavingPassword(
          true,
        );


        await profileService
          .changePassword(
            request,
          );


        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          newPasswordConfirm: '',
        });


        setShowCurrentPassword(
          false,
        );


        setShowNewPassword(
          false,
        );


        setShowNewPasswordConfirm(
          false,
        );


        showToast(
          'success',
          '비밀번호가 변경되었습니다.',
        );

      } catch (
        error: unknown
      ) {
        console.error(
          '비밀번호 변경 실패:',
          error,
        );


        let message =
          getErrorMessage(
            error,
            '비밀번호 변경에 실패했습니다.',
          );


        const status =
          getErrorStatus(
            error,
          );


        if (
          status ===
          400
        ) {
          message =
            '현재 비밀번호가 올바르지 않습니다.';
        }


        if (
          status ===
          401
        ) {
          message =
            '로그인 정보가 만료되었습니다. 다시 로그인해주세요.';
        }


        setPasswordError(
          message,
        );


        showToast(
          'error',
          message,
        );

      } finally {
        setIsSavingPassword(
          false,
        );
      }
    };


  /**
   * =========================================================
   * 프로필 되돌리기
   * =========================================================
   */
  const resetProfileForm =
    (): void => {
      if (
        !user
      ) {
        return;
      }


      setProfileForm({
        userNickname:
          user.userNickname,

        storeId:
          user.storeId,
      });


      setNicknameStatus(
        'available',
      );


      setNicknameMessage(
        '현재 사용 중인 닉네임입니다.',
      );


      setProfileError(
        '',
      );
    };


  /**
   * =========================================================
   * Password Input
   * =========================================================
   */
  const renderPasswordInput = ({
    id,
    label,
    value,
    placeholder,
    visible,
    onVisibleChange,
    onChange,
    autoComplete,
  }: PasswordInputProps):
    React.ReactNode => {
    return (
      <div>

        <label
          htmlFor={id}
          className={
            labelClassName
          }
        >
          {label}

          <span className="ml-1 text-red-500">
            *
          </span>
        </label>


        <div className="relative">

          <input
            id={id}
            name={id}
            type={
              visible
                ? 'text'
                : 'password'
            }
            value={value}
            placeholder={
              placeholder
            }
            autoComplete={
              autoComplete
            }
            disabled={
              isSavingPassword
            }
            onChange={(
              event,
            ) => {
              onChange(
                event
                  .target
                  .value,
              );


              setPasswordError(
                '',
              );
            }}
            className={`${inputClassName} pr-12`}
          />


          <button
            type="button"
            onClick={
              onVisibleChange
            }
            disabled={
              isSavingPassword
            }
            className="
              absolute right-0 top-0 flex h-full
              w-11 items-center justify-center
              text-gray-400 transition
              hover:text-main
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
            aria-label={
              visible
                ? `${label} 숨기기`
                : `${label} 보기`
            }
          >

            {visible ? (

              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 3l18 18M10.6 10.7a2 2 0 002.7 2.7M9.9 4.2A10.4 10.4 0 0112 4c5 0 9 4.1 10 8a11.3 11.3 0 01-2.3 4.3M6.2 6.2A11.1 11.1 0 002 12c1 3.9 5 8 10 8a10.7 10.7 0 005-1.2"
                />
              </svg>

            ) : (

              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                />

                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
              </svg>

            )}

          </button>

        </div>

      </div>
    );
  };


  /**
   * =========================================================
   * Loading
   * =========================================================
   */
  if (
    isLoading
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4">

        <div className="flex flex-col items-center gap-3">

          <span
            className="
              h-9 w-9 animate-spin rounded-full
              border-4 border-gray-200 border-t-main
            "
            aria-hidden="true"
          />


          <p className="text-sm text-gray-500">
            프로필 정보를 불러오고 있습니다.
          </p>

        </div>

      </main>
    );
  }


  /**
   * =========================================================
   * User Load Error
   * =========================================================
   */
  if (
    !user
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4">

        <section className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-sm">

          <div
            className="
              mx-auto flex h-14 w-14 items-center
              justify-center rounded-full bg-red-50
              text-red-500
            "
          >
            <svg
              className="h-7 w-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v4m0 4h.01M10.3 4.4L2.7 17.5A1.7 1.7 0 004.2 20h15.6a1.7 1.7 0 001.5-2.5L13.7 4.4a2 2 0 00-3.4 0z"
              />
            </svg>
          </div>


          <h1 className="mt-5 text-xl font-bold text-gray-900">
            프로필을 불러올 수 없습니다
          </h1>


          <p className="mt-2 text-sm leading-6 text-gray-500">
            {profileError ||
              '잠시 후 다시 시도해주세요.'}
          </p>


          <div className="mt-6 flex gap-2">

            <button
              type="button"
              onClick={() =>
                navigate(
                  -1,
                )
              }
              className="
                flex min-h-11 flex-1 items-center
                justify-center rounded-lg border
                border-gray-300 px-4 py-2.5
                text-sm font-semibold text-gray-700
                transition hover:bg-gray-50
              "
            >
              이전으로
            </button>


            <button
              type="button"
              onClick={() =>
                window.location.reload()
              }
              className="
                flex min-h-11 flex-1 items-center
                justify-center rounded-lg bg-main
                px-4 py-2.5 text-sm font-semibold
                text-white transition
                hover:bg-main-medium
              "
            >
              다시 시도
            </button>

          </div>

        </section>

      </main>
    );
  }


  return (
    <main className="min-h-screen bg-gray-50 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">

      {/* =====================================================
          Toast
          ===================================================== */}
      {toast && (

        <div
          className="
            fixed left-1/2 top-4 z-50
            w-[calc(100%-2rem)] max-w-sm
            -translate-x-1/2
            sm:left-auto sm:right-6 sm:top-6
            sm:w-auto sm:translate-x-0
          "
          role="status"
          aria-live="polite"
        >

          <div
            className={`
              flex items-center gap-3 rounded-xl
              border bg-white px-4 py-3
              text-sm font-medium shadow-lg
              ${
                toast.type ===
                'success'
                  ? `
                    border-emerald-200
                    text-emerald-700
                  `
                  : `
                    border-red-200
                    text-red-700
                  `
              }
            `}
          >

            <span
              className={`
                flex h-7 w-7 shrink-0
                items-center justify-center rounded-full
                ${
                  toast.type ===
                  'success'
                    ? `
                      bg-emerald-100
                      text-emerald-600
                    `
                    : `
                      bg-red-100
                      text-red-600
                    `
                }
              `}
            >
              {toast.type ===
              'success'
                ? '✓'
                : '!'}
            </span>


            {toast.message}

          </div>

        </div>

      )}


      <div className="mx-auto w-full max-w-2xl">

        {/* ===================================================
            Header
            =================================================== */}
        <header className="mb-6 flex items-start gap-3 sm:mb-8">

          <button
            type="button"
            onClick={() =>
              navigate(
                -1,
              )
            }
            className="
              mt-0.5 flex h-10 w-10 shrink-0
              items-center justify-center rounded-full
              text-gray-500 transition
              hover:bg-white hover:text-main
              hover:shadow-sm
            "
            aria-label="이전 페이지로 이동"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 18l-6-6 6-6"
              />
            </svg>
          </button>


          <div>

            <p className="text-xs font-bold tracking-[0.18em] text-main">
              MY PROFILE
            </p>


            <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              프로필 관리
            </h1>


            <p className="mt-1.5 text-sm leading-6 text-gray-500">
              회원 정보와 비밀번호를 관리할 수 있습니다.
            </p>

          </div>

        </header>


        {/* ===================================================
            Tabs
            =================================================== */}
        <div
          className="
            mb-5 grid grid-cols-2 rounded-xl
            border border-gray-200 bg-white p-1
            shadow-sm sm:mb-6
          "
          role="tablist"
          aria-label="프로필 설정 메뉴"
        >

          <button
            type="button"
            role="tab"
            aria-selected={
              activeTab ===
              'profile'
            }
            aria-controls="profile-tab-panel"
            id="profile-tab"
            onClick={() =>
              handleTabChange(
                'profile',
              )
            }
            className={`
              flex h-10 items-center
              justify-center gap-1.5 rounded-lg
              px-2 text-[13px] font-semibold
              transition sm:px-3 sm:text-sm
              ${
                activeTab ===
                'profile'
                  ? `
                    bg-main text-white shadow-sm
                  `
                  : `
                    text-gray-500
                    hover:bg-gray-50
                    hover:text-gray-900
                  `
              }
            `}
          >
            <svg
              className="h-3.5 w-3.5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.2 5.3l3.5 3.5M4 20l4.2-1 10.5-10.5a2.5 2.5 0 00-3.5-3.5L4.7 15.5 4 20z"
              />
            </svg>


            <span>
              기본 정보
            </span>

          </button>


          <button
            type="button"
            role="tab"
            aria-selected={
              activeTab ===
              'password'
            }
            aria-controls="password-tab-panel"
            id="password-tab"
            onClick={() =>
              handleTabChange(
                'password',
              )
            }
            className={`
              flex h-10 items-center
              justify-center gap-1.5 rounded-lg
              px-2 text-[13px] font-semibold
              transition sm:px-3 sm:text-sm
              ${
                activeTab ===
                'password'
                  ? `
                    bg-main text-white shadow-sm
                  `
                  : `
                    text-gray-500
                    hover:bg-gray-50
                    hover:text-gray-900
                  `
              }
            `}
          >
            <svg
              className="h-3.5 w-3.5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              aria-hidden="true"
            >
              <rect
                x="5"
                y="10"
                width="14"
                height="10"
                rx="2"
              />

              <path
                strokeLinecap="round"
                d="M8 10V7a4 4 0 018 0v3"
              />
            </svg>


            <span>
              비밀번호 변경
            </span>

          </button>

        </div>


        {/* ===================================================
            Profile Tab
            =================================================== */}
        {activeTab ===
          'profile' && (

          <section
            id="profile-tab-panel"
            role="tabpanel"
            aria-labelledby="profile-tab"
            className="
              rounded-2xl border border-gray-100
              bg-white p-5 shadow-sm sm:p-7
            "
          >

            <div className="mb-6">

              <h2 className="text-lg font-bold text-gray-900">
                기본 정보
              </h2>


              <p className="mt-1 text-sm leading-6 text-gray-500">
                아이디와 이름은 확인만 가능하며,
                닉네임과 소속 매장을 변경할 수 있습니다.
              </p>

            </div>


            <form
              onSubmit={
                handleProfileSubmit
              }
              className="space-y-5"
              noValidate
            >

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                <div>

                  <label
                    htmlFor="userId"
                    className={
                      labelClassName
                    }
                  >
                    아이디
                  </label>


                  <input
                    id="userId"
                    type="text"
                    value={
                      user.userId
                    }
                    readOnly
                    className={
                      readOnlyInputClassName
                    }
                  />

                </div>


                <div>

                  <label
                    htmlFor="userName"
                    className={
                      labelClassName
                    }
                  >
                    이름
                  </label>


                  <input
                    id="userName"
                    type="text"
                    value={
                      user.userName
                    }
                    readOnly
                    className={
                      readOnlyInputClassName
                    }
                  />

                </div>

              </div>


              {/* Nickname */}
              <div>

                <label
                  htmlFor="userNickname"
                  className={
                    labelClassName
                  }
                >
                  닉네임

                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>


                <div className="flex w-full items-stretch gap-2">

                  <input
                    id="userNickname"
                    name="userNickname"
                    type="text"
                    value={
                      profileForm
                        .userNickname
                    }
                    placeholder="닉네임 2~12자"
                    maxLength={12}
                    disabled={
                      isSavingProfile
                    }
                    onChange={(
                      event,
                    ) =>
                      handleNicknameChange(
                        event.target
                          .value,
                      )
                    }
                    className={`
                      ${inputClassName}
                      min-w-0 flex-1
                    `}
                  />


                  <button
                    type="button"
                    onClick={() => {
                      void handleNicknameCheck();
                    }}
                    disabled={
                      isSavingProfile ||
                      nicknameStatus ===
                        'checking'
                    }
                    className={`
                      flex min-h-11 w-24 shrink-0
                      items-center justify-center
                      whitespace-nowrap rounded-lg
                      border px-2 py-2.5
                      text-xs font-semibold transition
                      sm:min-h-12 sm:w-28
                      sm:rounded-xl sm:text-sm
                      ${
                        nicknameStatus ===
                        'available'
                          ? `
                            border-emerald-500
                            bg-emerald-50
                            text-emerald-700
                          `
                          : `
                            border-gray-300
                            bg-gray-100
                            text-gray-700
                            hover:border-main
                            hover:bg-main/5
                            hover:text-main
                          `
                      }
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                    `}
                  >

                    {nicknameStatus ===
                      'checking' && (

                      <span
                        className="
                          mr-1.5 h-3.5 w-3.5
                          animate-spin rounded-full
                          border-2 border-gray-400
                          border-t-transparent
                        "
                        aria-hidden="true"
                      />

                    )}


                    {nicknameStatus ===
                    'available'
                      ? '확인 완료'
                      : nicknameStatus ===
                          'checking'
                        ? '확인 중'
                        : nicknameStatus ===
                            'duplicate'
                          ? '재확인'
                          : '중복확인'}

                  </button>

                </div>


                {nicknameMessage && (

                  <p
                    className={`
                      mt-1.5 text-xs sm:text-sm
                      ${
                        nicknameStatus ===
                        'available'
                          ? 'text-emerald-600'
                          : nicknameStatus ===
                              'duplicate'
                            ? 'text-red-600'
                            : 'text-gray-500'
                      }
                    `}
                  >
                    {nicknameMessage}
                  </p>

                )}

              </div>


              {/* Store */}
              <div>

                <label
                  htmlFor="storeId"
                  className={
                    labelClassName
                  }
                >
                  소속 매장

                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>


                <div className="relative">

                  <select
                    id="storeId"
                    name="storeId"
                    value={
                      profileForm.storeId >
                      0
                        ? String(
                            profileForm.storeId,
                          )
                        : ''
                    }
                    disabled={
                      isSavingProfile ||
                      isLoadingStores ||
                      storeList.length ===
                        0
                    }
                    onChange={(
                      event,
                    ) => {
                      setProfileForm(
                        (
                          previous,
                        ) => ({
                          ...previous,

                          storeId:
                            event.target.value
                              ? Number(
                                  event.target.value,
                                )
                              : 0,
                        }),
                      );


                      setProfileError(
                        '',
                      );
                    }}
                    className={`
                      ${inputClassName}
                      appearance-none pr-10
                    `}
                  >

                    <option
                      value=""
                      disabled
                    >
                      {isLoadingStores
                        ? '매장 목록을 불러오는 중입니다'
                        : '소속 매장을 선택해주세요'}
                    </option>


                    {storeList.map(
                      (
                        store,
                      ) => (

                        <option
                          key={
                            store.storeId
                          }
                          value={
                            store.storeId
                          }
                        >
                          {
                            store.storeName
                          }
                        </option>

                      ),
                    )}

                  </select>


                  <svg
                    className="
                      pointer-events-none absolute
                      right-3 top-1/2 h-5 w-5
                      -translate-y-1/2
                      text-gray-400
                    "
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.09 1.03l-4.25 4.5a.75.75 0 01-1.09 0l-4.25-4.5a.75.75 0 01.02-1.05z"
                    />
                  </svg>

                </div>


                {isLoadingStores && (

                  <p className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">

                    <span
                      className="
                        h-3.5 w-3.5
                        animate-spin rounded-full
                        border-2 border-gray-300
                        border-t-main
                      "
                      aria-hidden="true"
                    />

                    매장 목록을 불러오고 있습니다.

                  </p>

                )}


                {!isLoadingStores &&
                  storeError && (

                  <div className="mt-2 flex items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2">

                    <p className="text-xs leading-5 text-red-600">
                      {storeError}
                    </p>


                    <button
                      type="button"
                      onClick={() => {
                        void fetchStores();
                      }}
                      className="
                        shrink-0 text-xs
                        font-semibold text-red-700
                        underline underline-offset-2
                      "
                    >
                      다시 시도
                    </button>

                  </div>

                )}

              </div>


              {profileError && (

                <div
                  role="alert"
                  aria-live="polite"
                  className="
                    rounded-lg bg-red-50
                    px-3.5 py-2.5 text-sm
                    leading-5 text-red-600
                  "
                >
                  {profileError}
                </div>

              )}


              <div className="flex gap-2 pt-1">

                <button
                  type="button"
                  onClick={
                    resetProfileForm
                  }
                  disabled={
                    isSavingProfile ||
                    !profileChanged
                  }
                  className="
                    flex min-h-11 flex-1
                    items-center justify-center
                    rounded-lg border
                    border-gray-300 px-4
                    py-2.5 text-sm font-semibold
                    text-gray-700 transition
                    hover:bg-gray-50
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                    sm:min-h-12 sm:rounded-xl
                  "
                >
                  되돌리기
                </button>


                <button
                  type="submit"
                  disabled={
                    isSavingProfile ||
                    !profileChanged ||
                    isLoadingStores
                  }
                  className="
                    flex min-h-11 flex-[1.5]
                    items-center justify-center
                    rounded-lg bg-main px-4
                    py-2.5 text-sm font-semibold
                    text-white transition
                    hover:bg-main-medium
                    focus:outline-none focus:ring-2
                    focus:ring-main/30
                    focus:ring-offset-2
                    disabled:cursor-not-allowed
                    disabled:opacity-60
                    sm:min-h-12 sm:rounded-xl
                  "
                >

                  {isSavingProfile ? (

                    <span className="flex items-center gap-2">

                      <span
                        className="
                          h-4 w-4 animate-spin
                          rounded-full border-2
                          border-white/40
                          border-t-white
                        "
                        aria-hidden="true"
                      />

                      저장 중

                    </span>

                  ) : (
                    '변경사항 저장'
                  )}

                </button>

              </div>

            </form>

          </section>

        )}


        {/* ===================================================
            Password Tab
            =================================================== */}
        {activeTab ===
          'password' && (

          <section
            id="password-tab-panel"
            role="tabpanel"
            aria-labelledby="password-tab"
            className="
              rounded-2xl border border-gray-100
              bg-white p-5 shadow-sm sm:p-7
            "
          >

            <div className="mb-6">

              <h2 className="text-lg font-bold text-gray-900">
                비밀번호 변경
              </h2>


              <p className="mt-1 text-sm leading-6 text-gray-500">
                현재 비밀번호를 확인한 후 새로운
                비밀번호로 변경합니다.
              </p>

            </div>


            <form
              onSubmit={
                handlePasswordSubmit
              }
              className="space-y-5"
              noValidate
            >

              {renderPasswordInput({
                id:
                  'currentPassword',

                label:
                  '현재 비밀번호',

                value:
                  passwordForm
                    .currentPassword,

                placeholder:
                  '현재 비밀번호를 입력해주세요',

                visible:
                  showCurrentPassword,

                onVisibleChange:
                  () =>
                    setShowCurrentPassword(
                      (
                        previous,
                      ) =>
                        !previous,
                    ),

                onChange:
                  (
                    value,
                  ) =>
                    setPasswordForm(
                      (
                        previous,
                      ) => ({
                        ...previous,

                        currentPassword:
                          value,
                      }),
                    ),

                autoComplete:
                  'current-password',
              })}


              {renderPasswordInput({
                id:
                  'newPassword',

                label:
                  '새 비밀번호',

                value:
                  passwordForm
                    .newPassword,

                placeholder:
                  '영문, 숫자 포함 8자 이상',

                visible:
                  showNewPassword,

                onVisibleChange:
                  () =>
                    setShowNewPassword(
                      (
                        previous,
                      ) =>
                        !previous,
                    ),

                onChange:
                  (
                    value,
                  ) =>
                    setPasswordForm(
                      (
                        previous,
                      ) => ({
                        ...previous,

                        newPassword:
                          value,
                      }),
                    ),

                autoComplete:
                  'new-password',
              })}


              {renderPasswordInput({
                id:
                  'newPasswordConfirm',

                label:
                  '새 비밀번호 확인',

                value:
                  passwordForm
                    .newPasswordConfirm,

                placeholder:
                  '새 비밀번호를 다시 입력해주세요',

                visible:
                  showNewPasswordConfirm,

                onVisibleChange:
                  () =>
                    setShowNewPasswordConfirm(
                      (
                        previous,
                      ) =>
                        !previous,
                    ),

                onChange:
                  (
                    value,
                  ) =>
                    setPasswordForm(
                      (
                        previous,
                      ) => ({
                        ...previous,

                        newPasswordConfirm:
                          value,
                      }),
                    ),

                autoComplete:
                  'new-password',
              })}


              {newPasswordMismatch && (

                <p className="-mt-3 text-xs text-red-600 sm:text-sm">
                  새 비밀번호가 일치하지 않습니다.
                </p>

              )}


              {newPasswordMatches && (

                <p className="-mt-3 text-xs text-emerald-600 sm:text-sm">
                  새 비밀번호가 일치합니다.
                </p>

              )}


              <div
                className="
                  rounded-xl border border-gray-200
                  bg-gray-50 px-4 py-3
                "
              >

                <p className="text-xs font-semibold text-gray-700">
                  비밀번호 설정 안내
                </p>


                <p className="mt-1 text-xs leading-5 text-gray-500">
                  영문과 숫자를 포함하여 8자 이상으로
                  입력해주세요. 현재 비밀번호와 다른
                  비밀번호를 사용해야 합니다.
                </p>

              </div>


              {passwordError && (

                <div
                  role="alert"
                  aria-live="polite"
                  className="
                    rounded-lg bg-red-50
                    px-3.5 py-2.5 text-sm
                    leading-5 text-red-600
                  "
                >
                  {passwordError}
                </div>

              )}


              <button
                type="submit"
                disabled={
                  isSavingPassword
                }
                className="
                  flex min-h-11 w-full
                  items-center justify-center
                  rounded-lg bg-gray-900
                  px-4 py-2.5 text-sm
                  font-semibold text-white
                  transition hover:bg-black
                  focus:outline-none focus:ring-2
                  focus:ring-gray-900/30
                  focus:ring-offset-2
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                  sm:min-h-12 sm:rounded-xl
                "
              >

                {isSavingPassword ? (

                  <span className="flex items-center gap-2">

                    <span
                      className="
                        h-4 w-4 animate-spin
                        rounded-full border-2
                        border-white/40
                        border-t-white
                      "
                      aria-hidden="true"
                    />

                    변경 중

                  </span>

                ) : (
                  '비밀번호 변경'
                )}

              </button>

            </form>

          </section>

        )}

      </div>

    </main>
  );
}